import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";

import { ChevronIcon } from "../../icons";
import {
  getReviewsForPage,
  groupReviewsByPlacedMonthTabs,
  PLACED_MONTH_TABS,
} from "../../utils/reviewUtils";
import PlacedStudentCardDark from "../PlacedStudentCardDark/PlacedStudentCardDark";
import useIsMobilePlacedNav from "../../utils/useIsMobilePlacedNav";

import "./RecentlyPlacedLearnersDark.css";

const DESKTOP_PER_VIEW = 3;

function PlacedNavPrevIcon({ active }) {
  return <ChevronIcon active={active} direction="prev" />;
}

function PlacedNavNextIcon({ active }) {
  return <ChevronIcon active={active} direction="next" />;
}

function MonthSlider({ reviews, isActive }) {
  const isMobileNav = useIsMobilePlacedNav();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [maxIdx, setMaxIdx] = useState(0);
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: false,
      rubberband: false,
      initial: 0,
      defaultAnimation: { duration: 450 },
      slides: {
        perView: DESKTOP_PER_VIEW,
        spacing: 24,
        origin: "auto",
      },
      breakpoints: {
        "(max-width: 991px)": {
          slides: { perView: "auto", spacing: 16, origin: 0 },
        },
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
        setMaxIdx(slider.track.details.maxIdx);
      },
      created(slider) {
        setMaxIdx(slider.track.details.maxIdx);
        slider.moveToIdx(0, true);
      },
      updated(slider) {
        setMaxIdx(slider.track.details.maxIdx);
      },
    },
    []
  );

  const refreshSlider = useCallback(() => {
    instanceRef.current?.update();
    instanceRef.current?.moveToIdx(0, true);
    const details = instanceRef.current?.track?.details;
    if (details) {
      setMaxIdx(details.maxIdx);
      setCurrentSlide(details.rel);
    }
  }, [instanceRef]);

  useLayoutEffect(() => {
    if (!isActive) return undefined;
    setCurrentSlide(0);
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      refreshSlider();
    };
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const t = setTimeout(run, 100);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      clearTimeout(t);
    };
  }, [isActive, reviews, refreshSlider]);

  useEffect(() => {
    const onResize = () => {
      instanceRef.current?.update();
      const details = instanceRef.current?.track?.details;
      if (details) setMaxIdx(details.maxIdx);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [instanceRef]);

  if (!reviews.length) {
    return (
      <div className="w-dyn-empty">
        <div>No items found.</div>
      </div>
    );
  }

  const atStart = currentSlide <= 0;
  const atEnd = currentSlide >= maxIdx;
  const mobileProgress = maxIdx > 0 ? currentSlide / maxIdx : 0;
  const showMobileProgress = isMobileNav && reviews.length > 1;

  return (
    <>
      <div className="tabs-swiper myswiper-1">
        <div ref={sliderRef} className="card-wrapper-placed-students keen-slider">
          {reviews.map((review) => (
            <PlacedStudentCardDark key={review.UID || review.slug} review={review} />
          ))}
        </div>
      </div>
      <div className="div-block-1831980521448684 placed-learners-nav">
        <div className="div-block-1831980521448614-2 placed-learners-arrows">
          {!isMobileNav && (
            <>
              <button
                type="button"
                className={`prev-btn-tabs-off swiper-button-prev-1 track-button${atStart ? " is-disabled" : ""}`}
                onClick={() => instanceRef.current?.prev()}
                disabled={atStart}
                aria-label="Previous placed students"
              >
                <PlacedNavPrevIcon active={!atStart} />
              </button>
              <button
                type="button"
                className={`nxt-btn-tabs-off swiper-button-next-1 track-button${atEnd ? " is-disabled" : ""}`}
                onClick={() => instanceRef.current?.next()}
                disabled={atEnd}
                aria-label="Next placed students"
              >
                <PlacedNavNextIcon active={!atEnd} />
              </button>
            </>
          )}
          {showMobileProgress && (
            <div
              className="placed-nav-progress"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={maxIdx + 1}
              aria-valuenow={currentSlide + 1}
              aria-label="Carousel progress"
            >
              <span
                className="placed-nav-progress__thumb"
                style={{ "--progress": mobileProgress }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function RecentlyPlacedLearnersDark() {
  const monthGroups = useMemo(
    () => groupReviewsByPlacedMonthTabs(getReviewsForPage()),
    []
  );
  const [activeKey, setActiveKey] = useState(PLACED_MONTH_TABS[0].key);

  return (
    <section id="recently-placed" className="students-placed-section-offline students-placed-section-dark">
      <div className="student-placed-container-online">
        <h2 className="h2-heading">Recent Student Placements</h2>
        <div data-easing="ease" data-duration-in="300" data-duration-out="100" className="tabs-48 w-tabs">
          <div className="placed-learners-tabs w-tab-menu" role="tablist">
            {monthGroups.map((group) => (
              <button
                key={group.key}
                type="button"
                role="tab"
                aria-selected={activeKey === group.key}
                className={`tabs-for-placed-students track-button w-inline-block w-tab-link${
                  activeKey === group.key ? " w--current" : ""
                }`}
                onClick={() => setActiveKey(group.key)}
              >
                <div>{group.label}</div>
              </button>
            ))}
          </div>
          <div className="w-tab-content">
            {monthGroups.map((group) => (
              <div
                key={group.key}
                className={`placed-students-tab-container w-tab-pane${
                  activeKey === group.key ? " w--tab-active" : ""
                }`}
              >
                {activeKey === group.key ? (
                  <MonthSlider key={group.key} reviews={group.reviews} isActive />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
